import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Grid, Stack, Typography } from '@mui/material';
import { useEffect, useId, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  EditableLegalClientSchema,
  IEditableLegalClient,
} from '../../../../../Interfaces/IEditLegalClient';
import {
  getEmptyLegalClient,
  ILegalClient,
} from '../../../../../Interfaces/ILegalClient';
import { ILegalTransactionRelationship } from '../../../../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationship';
import { LegalTransactionRelationshipRoleEnum } from '../../../../../Interfaces/ILegalTransactionRelationship/LegalTransactionRelationshipRoleEnum';
import {
  getEmptyMemberSpouseData,
  getEmptySpouseMember,
  IMemberSpouse,
} from '../../../../../Interfaces/ILegalTransactionRelationship/Members/IMemberSpouse';
import {
  getEmptyTestatorMember,
  getEmptyTestatorMemberData,
  IMemberTestator,
  IMemberTestatorData,
  TestatorMemberDataSchema,
} from '../../../../../Interfaces/ILegalTransactionRelationship/Members/IMemberTestament';
import { getEmptyNaturalPerson } from '../../../../../Interfaces/INaturalPerson';
import { CustomeSlide } from '../../../../core/CustomeSlide';
import { useDialogContext } from '../../../../core/DialogPresenter/DialogContext';
import { useRegisterDialogPage } from '../../../../core/DialogPresenter/useRegisterDialogPage';
import { EditLegalClient } from '../../../../LegalClient/EditLegalClient';
import { useLegalClientStore } from '../../generic/legalClientStore';
import { NavRow } from '../../generic/NavRow';
import {
  isSpouseTestatorMember,
  removeLegalClientFromRelationships,
  updateOrCreateMember,
  updateOrCreateRelationship,
} from '../../generic/utilities';
import {
  getOrCreateCurrentMarriageRelationship,
  getOrCreateTestamentRelationship,
  isSpouseMember,
} from '../../Inherit/utilities';
import { TestatorMemberInformation } from '../components/TestamentTestatorInformation';

export type ITestamentSecondaryTestatorProps = {
  relationshipArray: ILegalTransactionRelationship[];
  setRelationshipArray: React.Dispatch<
    React.SetStateAction<ILegalTransactionRelationship[]>
  >;
};

export const TestamentSecondaryTestator: React.FC<
  ITestamentSecondaryTestatorProps
> = ({ relationshipArray, setRelationshipArray }) => {
  const id = useId();
  const { handleNext, handleBack, isNextDisabled } = useDialogContext();

  const [hasSecondaryTestator, setHasSecondaryTestator] = useState(false);

  const [legalClientArray, putLegalClient, getLegalClient, removeLegalClient] =
    useLegalClientStore(state => [
      state.legalClientArray,
      state.putLegalClient,
      state.getLegalClient,
      state.removeLegalClient,
    ]);

  const secondaryTestator = useMemo(
    () =>
      getOrCreateTestamentRelationship(relationshipArray).Members.find(
        isSpouseTestatorMember
      ) ||
      getEmptyTestatorMember(
        {
          ...getEmptyLegalClient(),
          ...getEmptyNaturalPerson(),
          idLegalClient: 1,
        },
        1,
        getEmptyTestatorMemberData('spouse')
      ),
    [relationshipArray, legalClientArray]
  );

  const legalClient = getLegalClient(secondaryTestator.idLegalClient);

  useEffect(() => {
    if (legalClient) {
      setHasSecondaryTestator(true);
    } else {
      setHasSecondaryTestator(false);
    }
  }, [secondaryTestator]);

  const legalClientMethods = useForm<IEditableLegalClient>({
    mode: 'onChange',
    defaultValues: (legalClient as IEditableLegalClient) || {
      ...getEmptyLegalClient(),
      ...getEmptyNaturalPerson(),
      idLegalClient: 1,
    },
    resolver: zodResolver(EditableLegalClientSchema),
  });

  const memberDataMethods = useForm<IMemberTestatorData>({
    mode: 'onChange',
    defaultValues:
      secondaryTestator.data || getEmptyTestatorMemberData('spouse'),
    resolver: zodResolver(TestatorMemberDataSchema),
  });

  const pageNumber = useRegisterDialogPage(id, {
    title: 'Ehegatte / eingetragene Lebenspartner',
    disableNext: async () => {
      if (!hasSecondaryTestator) {
        return false;
      }
      const legalClientValid = legalClientMethods.formState.isValid;
      const memberDataValid = memberDataMethods.formState.isValid;

      return !(legalClientValid && memberDataValid);
    },
  });

  async function onSubmit() {
    if (!hasSecondaryTestator) {
      handleNext();
      return;
    }

    const legalClientValid = legalClientMethods.trigger();
    const memberDataValid = memberDataMethods.trigger();

    const results = await Promise.all([legalClientValid, memberDataValid]);

    const allValid = results.every(valid => valid);
    if (allValid) {
      const formLegalClient = legalClientMethods.getValues() as ILegalClient;
      const updatedLegalClient = putLegalClient(formLegalClient);

      const updatedMemberData = memberDataMethods.getValues();

      const inheritanceRelationship =
        getOrCreateTestamentRelationship(relationshipArray);
      const updatedTestator: IMemberTestator = {
        ...secondaryTestator,
        idRelationship: inheritanceRelationship.idRelationship,
        idLegalClient: updatedLegalClient.idLegalClient,
        data: updatedMemberData,
      };

      const updatedInheritanceRelationship: ILegalTransactionRelationship = {
        ...inheritanceRelationship,
        Members: updateOrCreateMember(
          inheritanceRelationship.Members,
          updatedTestator
        ),
      };

      const relationshipsWithUpdatedInheritance = updateOrCreateRelationship(
        relationshipArray,
        updatedInheritanceRelationship
      );

      const involvedRelationships = relationshipsWithUpdatedInheritance.filter(
        relationship =>
          relationship.Members.some(
            member => member.idLegalClient === updatedLegalClient.idLegalClient
          )
      );
      const marriageRelationship = getOrCreateCurrentMarriageRelationship(
        involvedRelationships
      );
      if (marriageRelationship.Members.length === 0) {
        const testatorAsSpouseMember = {
          ...inheritanceRelationship.Members[0],
          idRelationship: marriageRelationship.idRelationship,
          idRelationshipRole: LegalTransactionRelationshipRoleEnum.Spouse,
          data: getEmptyMemberSpouseData(),
        };
        marriageRelationship.Members.push(testatorAsSpouseMember);
      }
      const spouseMember = marriageRelationship.Members.find(
        isSpouseMember
      ) || {
        ...getEmptySpouseMember(
          updatedLegalClient,
          marriageRelationship.idRelationship
        ),
        idLegalClient: updatedLegalClient.idLegalClient,
      };
      const updatedSpouse: IMemberSpouse = {
        ...spouseMember,
        idRelationship: marriageRelationship.idRelationship,
        idLegalClient: updatedLegalClient.idLegalClient,
        data: getEmptyMemberSpouseData(),
      };

      const updatedRelationship: ILegalTransactionRelationship = {
        ...marriageRelationship,
        Members: updateOrCreateMember(
          marriageRelationship.Members,
          updatedSpouse
        ),
      };
      setRelationshipArray(
        updateOrCreateRelationship(
          relationshipsWithUpdatedInheritance,
          updatedRelationship
        )
      );

      handleNext();
    }
  }

  return pageNumber === null ? null : (
    <CustomeSlide key={id} pageNumber={pageNumber} appear={false}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant='h4' sx={{ mb: 2 }}>
            Ehegatte / eingetragene Lebenspartner
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Typography variant='body1' sx={{ mb: 2 }}>
            Hatte der Haupttestator zum Zeitpunkt seines Todes einen Ehegatten
            oder eingetragenen Lebenspartner, der ebenfalls als Testator im
            Testament berücksichtigt werden soll?
          </Typography>

          <Stack direction='row' gap={2}>
            <Button
              variant={hasSecondaryTestator ? 'outlined' : 'contained'}
              sx={{ mr: 1 }}
              onClick={() => {
                setHasSecondaryTestator(false);

                legalClientMethods.reset({
                  ...getEmptyLegalClient(),
                  ...getEmptyNaturalPerson(),
                  idLegalClient: 1,
                });
                memberDataMethods.reset(getEmptyTestatorMemberData('spouse'));
                removeLegalClient(secondaryTestator.idLegalClient);
                setRelationshipArray(prev =>
                  removeLegalClientFromRelationships(
                    prev,
                    secondaryTestator.idLegalClient
                  )
                );
              }}
            >
              Nein
            </Button>

            <Button
              variant={hasSecondaryTestator ? 'contained' : 'outlined'}
              sx={{ mr: 1 }}
              onClick={() => {
                setHasSecondaryTestator(true);
              }}
            >
              Ja
            </Button>
          </Stack>
        </Grid>

        {hasSecondaryTestator && (
          <>
            <Grid item xs={12}>
              <FormProvider {...legalClientMethods}>
                <form
                  id='testator-legal-client-form'
                  // onSubmit={onSubmitLegalClient}
                  noValidate
                >
                  <EditLegalClient disableCompanyLegalClient />
                </form>
              </FormProvider>
            </Grid>

            <Grid item xs={12}>
              <FormProvider {...memberDataMethods}>
                <form
                  id='testator-member-data-form'
                  // onSubmit={onSubmitMemberData}
                  noValidate
                >
                  <Typography variant='h6' sx={{ mb: 2 }}>
                    Informationen zur Testamentsausstellung
                  </Typography>
                  <TestatorMemberInformation />
                </form>
              </FormProvider>
            </Grid>
          </>
        )}

        <Grid item xs={12}>
          <NavRow handleNext={onSubmit} handleBack={handleBack} />
        </Grid>
      </Grid>
    </CustomeSlide>
  );
};
